import LegalDocumentBody from "@/components/legal/LegalDocumentBody";
import { Button } from "@/domains/shared/components/Button";
import {
    LEGAL_DOCUMENT_CHROME_TRANSLATIONS,
    type LegalSection,
} from "@/domains/shared/constants/legalDocuments";
import { cn } from "@/domains/shared/services/cn";
import { getTranslation } from "@mivo/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
} from "@/domains/shared/components/Dialog";

const SCROLL_END_THRESHOLD_PX = 24;

type LegalDocumentAcceptDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    sections: LegalSection[];
    version: string;
    effectiveDate: string;
    onAccept: () => void;
    testIdPrefix: string;
};

export default function LegalDocumentAcceptDialog({
    open,
    onOpenChange,
    title,
    description,
    sections,
    version,
    effectiveDate,
    onAccept,
    testIdPrefix,
}: LegalDocumentAcceptDialogProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrolledToEnd, setScrolledToEnd] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;

        if (!el) {
            return;
        }

        const atEnd =
            el.scrollHeight <= el.clientHeight + SCROLL_END_THRESHOLD_PX ||
            el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_END_THRESHOLD_PX;

        setScrolledToEnd(atEnd);
    }, []);

    useEffect(() => {
        if (!open) {
            setScrolledToEnd(false);
            return;
        }

        const frame = requestAnimationFrame(() => {
            updateScrollState();
        });

        return () => cancelAnimationFrame(frame);
    }, [open, sections, updateScrollState]);

    const handleAccept = () => {
        onAccept();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="rounded-3xl w-full max-w-[95vw] md:max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
                data-testid={`${testIdPrefix}-dialog`}
            >
                <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                    <h2 slot="title" className={cn("text-lg font-semibold leading-none tracking-tight")}>
                        {title}
                    </h2>
                    <DialogDescription className="font-bold text-destructive">{description}</DialogDescription>
                </DialogHeader>

                <DialogBody
                    ref={scrollRef}
                    onScroll={updateScrollState}
                    data-testid={`${testIdPrefix}-scroll`}
                >
                    <LegalDocumentBody
                        sections={sections}
                        version={version}
                        effectiveDate={effectiveDate}
                    />
                </DialogBody>

                <DialogFooter className="shrink-0 border-t border-border px-6 py-4 gap-2 sm:gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid={`${testIdPrefix}-cancel`}
                        className="w-full"
                    >
                        {getTranslation(LEGAL_DOCUMENT_CHROME_TRANSLATIONS.closeButton)}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleAccept}
                        disabled={!scrolledToEnd}
                        data-testid={`${testIdPrefix}-accept`}
                        data-scrolled-to-end={scrolledToEnd ? "true" : "false"}
                        className="w-full"
                    >
                        {getTranslation(LEGAL_DOCUMENT_CHROME_TRANSLATIONS.acceptButton)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
